import React, { createContext, useEffect, useState } from 'react';
import { Dimensions } from 'react-native';
import DataOps from "../../data/DataOps";
import NotificationOps from "../../data/NotificationOps";
import { User, ChildProfile } from "../../interface/User";
import { isParentAccount } from "../../interface/AccountType";

const GlobalStateContext = createContext(null);

const WIDTH = Dimensions.get('window').width;

const GlobalStateProvider = ({ children }) => {
    const [padding, setPadding] = useState(WIDTH >= 1200 ? (WIDTH - 1200) / 2 : 100);
    const [notifications, setNotifications] = useState([]);

    const [isHostingView, updateHostingView] = useState(false);
    const setHostingView = (isHostingView: boolean) => {
        updateHostingView(isHostingView)
    };

    const [user, updateUser] = useState<User | null>(null);
    const [userCollection, setUserCollection] = useState<string>('user');
    const setUser = (newUser: User | null) => updateUser(newUser);

    const [isLoadingUser, setIsLoadingUser] = useState(true);

    const [activeChild, updateActiveChild] = useState<ChildProfile | null>(null);
    const setActiveChild = (childId: string | null) => {
        if (!user || !childId || !isParentAccount(user.accountType)) {
            updateActiveChild(null);
            return;
        }
        const child = user.children?.find(c => c.childId === childId);
        updateActiveChild(child || null);

        // Update user's activeChildId (only for family accounts)
        const updatedUser = { ...user, activeChildId: childId };
        updateUser(updatedUser);
    };

    const [likedCamps, updateLikedCamps] = useState(new Set());
    const setLikedCamps = (newLikedCamps) => updateLikedCamps(newLikedCamps);

    const [likedSessions, updateLikedSessions] = useState(new Set());
    const setLikedSessions = (newLikedSessions) => updateLikedSessions(newLikedSessions);

    const [userCampId, updateUserCampId] = useState('');
    const setUserCampId = (newUserCampId) => updateUserCampId(newUserCampId);

    const [globalCamps, updateCamps] = useState([]);
    const setGlobalCamps = (newCamps) => updateCamps(newCamps);

    const [globalSessions, updateSessions] = useState([]);
    const setGlobalSessions = (newSessions) => updateSessions(newSessions);

    const [showLoginModal, setShowLoginModal] = useState(false);

    // Helper to load from localStorage synchronously
    const loadFromLocalStorage = (key: string) => {
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : null;
        } catch (error) {
            console.error(`Error loading ${key} from storage:`, error);
            return null;
        }
    };

    // Load cached data synchronously during initialization
    const initialMapCenter = loadFromLocalStorage('cachedMapCenter');
    const initialSearch = (() => {
        const cached = loadFromLocalStorage('cachedSearch');
        if (cached) {
            // Convert date strings back to Date objects
            if (cached.startDate) cached.startDate = new Date(cached.startDate);
            if (cached.endDate) cached.endDate = new Date(cached.endDate);
        }
        return cached;
    })();
    const initialLocation = (() => {
        const cached = loadFromLocalStorage('cachedLocation');
        if (cached && cached.timestamp) {
            const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days
            if (Date.now() - cached.timestamp < maxAge) {
                return cached;
            } else {
                // Location is too old, remove it
                try {
                    localStorage.removeItem('cachedLocation');
                } catch { }
                return null;
            }
        }
        return cached;
    })();
    const initialIsMapView = (() => {
        const cached = loadFromLocalStorage('isMapView');
        return cached !== null ? cached : false; // Default to list view
    })();

    // Global map center to allow search box to recenter map view (with persistence)
    const [globalMapCenter, updateGlobalMapCenter] = useState<{ lat: number; lng: number } | null>(initialMapCenter);

    const setGlobalMapCenter = (center: { lat: number; lng: number } | null) => {
        updateGlobalMapCenter(center);
        // Persist to localStorage
        if (center) {
            try {
                localStorage.setItem('cachedMapCenter', JSON.stringify(center));
            } catch (error) {
                console.error('Error saving map center to storage:', error);
            }
        } else {
            try {
                localStorage.removeItem('cachedMapCenter');
            } catch (error) {
                console.error('Error removing map center from storage:', error);
            }
        }
    };

    // Last-used search criteria for collapsed search summary (with persistence)
    const [globalSearch, updateGlobalSearch] = useState<{
        address?: string;
        startDate?: Date | null;
        endDate?: Date | null;
        age?: number | null;
        gender?: string | null;
    } | null>(initialSearch);

    const setGlobalSearch = (search: typeof globalSearch) => {
        updateGlobalSearch(search);
        // Persist to localStorage
        if (search) {
            try {
                localStorage.setItem('cachedSearch', JSON.stringify(search));
            } catch (error) {
                console.error('Error saving search to storage:', error);
            }
        } else {
            try {
                localStorage.removeItem('cachedSearch');
            } catch (error) {
                console.error('Error removing search from storage:', error);
            }
        }
    };

    // Persisted user location to avoid re-fetching on every page load
    const [globalLocation, updateGlobalLocation] = useState<{
        coords: { latitude: number; longitude: number };
        timestamp?: number;
    } | null>(initialLocation);

    const setGlobalLocation = (location: { coords: { latitude: number; longitude: number }; timestamp?: number } | null, updateMapCenter: boolean = false) => {
        updateGlobalLocation(location);
        // Persist to localStorage
        if (location) {
            try {
                localStorage.setItem('cachedLocation', JSON.stringify(location));
                // Only update map center if explicitly requested (e.g., when clicking "My Location" button)
                if (updateMapCenter) {
                    setGlobalMapCenter({
                        lat: location.coords.latitude,
                        lng: location.coords.longitude
                    });
                }
            } catch (error) {
                console.error('Error saving location to storage:', error);
            }
        } else {
            try {
                localStorage.removeItem('cachedLocation');
            } catch (error) {
                console.error('Error removing location from storage:', error);
            }
        }
    };

    // Persisted view preference (map vs list)
    const [isMapView, updateIsMapView] = useState(initialIsMapView);

    const setIsMapView = (isMap: boolean) => {
        updateIsMapView(isMap);
        try {
            localStorage.setItem('isMapView', JSON.stringify(isMap));
        } catch (error) {
            console.error('Error saving view preference to storage:', error);
        }
    };

    const value = {
        padding: padding,
        user: user, setUser: setUser,
        userCollection: userCollection, setUserCollection: setUserCollection,
        isLoadingUser: isLoadingUser,
        activeChild: activeChild, setActiveChild: setActiveChild,
        userCampId: userCampId, setUserCampId: setUserCampId,
        likedCamps: likedCamps, setLikedCamps: setLikedCamps,
        likedSessions: likedSessions, setLikedSessions: setLikedSessions,
        isHostingView: isHostingView, setHostingView: setHostingView,
        globalCamps: globalCamps, setGlobalCamps: setGlobalCamps,
        globalSessions: globalSessions, setGlobalSessions: setGlobalSessions,
        notifications: notifications, setNotifications: setNotifications,
        showLoginModal: showLoginModal, setShowLoginModal: setShowLoginModal,
        globalMapCenter: globalMapCenter, setGlobalMapCenter: setGlobalMapCenter,
        globalSearch: globalSearch, setGlobalSearch: setGlobalSearch,
        globalLocation: globalLocation, setGlobalLocation: setGlobalLocation,
        isMapView: isMapView, setIsMapView: setIsMapView,
    };

    useEffect(() => {
        const fetchUserFromStorage = async () => {
            try {
                const localUser = await DataOps.retrieveDataFromStorage('user');
                if (!localUser?.userId) {
                    setIsLoadingUser(false);
                    return;
                }

                let remoteUser: any = null;
                let foundCollection = localUser?.accountType
                    ? DataOps.getCollectionNameForAccountType(localUser.accountType)
                    : 'user';

                // Prefer the collection implied by accountType to avoid stale cross-collection reads.
                if (foundCollection) {
                    remoteUser = await DataOps.fetchDataFromFirebase(foundCollection, localUser.userId, true);
                }

                // Fallback to cross-collection lookup only if preferred collection misses.
                if (!remoteUser) {
                    const userResult = await DataOps.fetchUserFromAnyCollection(localUser.userId);
                    if (userResult) {
                        remoteUser = userResult.user;
                        foundCollection = userResult.collection;
                    }
                }

                if (remoteUser) {
                    setUser(remoteUser);
                    setUserCollection(foundCollection);
                    await DataOps.saveDataToStorage('user', remoteUser);

                    if (remoteUser) {
                        setLikedCamps(new Set(remoteUser.likedCamps || []));
                        setLikedSessions(new Set(remoteUser.likedSessions || []));
                    }

                    if (remoteUser.camps && remoteUser.camps.length > 0) {
                        setUserCampId(remoteUser.camps[0]);
                    }
                } else {
                    // Last-resort fallback to cached local user if backend lookup fails.
                    setUser(localUser);
                    setUserCollection(foundCollection || 'user');
                    setLikedCamps(new Set(localUser.likedCamps || []));
                    setLikedSessions(new Set(localUser.likedSessions || []));
                    if (localUser.camps && localUser.camps.length > 0) {
                        setUserCampId(localUser.camps[0]);
                    }
                }
            } catch (error) {
                console.error('Error fetching user from local storage:', error);
            } finally {
                setIsLoadingUser(false);
            }
        };

        const unused_response = fetchUserFromStorage();
        // Cached data is now loaded synchronously during state initialization
    }, []);

    useEffect(() => {
        if (!user?.userId || !user?.accountType) return;

        // Listen to user's personal notifications subcollection
        // Path: {collection}/{userId}/notifications/ (where collection is user/host/coach based on accountType)
        const unsubscribe = NotificationOps.listenToNotificationsForAnyAccount(
            user.userId,
            user.accountType,
            (newNotifications) => {
                setNotifications(newNotifications);
            },
            50 // Limit to 50 most recent notifications
        );

        return () => unsubscribe();
    }, [user?.userId, user?.accountType]);

    return (
        <GlobalStateContext.Provider value={value}>
            {children}
        </GlobalStateContext.Provider>
    );
};

export { GlobalStateContext, GlobalStateProvider };